import type { SubscriptionType } from '$lib/types/subscription'

export interface SubscriptionRequestMetadata {
  deviceId: string
  tier: SubscriptionType
  licenseKey: string | null
}

const DEVICE_ID_KEYS = ['deviceId', 'device_id', 'current_device_id'] as const

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined'
}

function generateDeviceId(): string {
  const timestamp = Date.now().toString(36)
  const randomStr = Math.random().toString(36).substring(2, 15)
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'

  let hash = 0
  for (let i = 0; i < userAgent.length; i++) {
    hash = ((hash << 5) - hash) + userAgent.charCodeAt(i)
    hash = hash & hash
  }

  return `${timestamp}-${randomStr}-${Math.abs(hash).toString(36)}`
}

export function getOrCreateDeviceId(): string {
  if (!hasLocalStorage()) {
    return 'unknown'
  }

  const existing = DEVICE_ID_KEYS
    .map(key => localStorage.getItem(key))
    .find((value): value is string => Boolean(value))

  const deviceId = existing || generateDeviceId()

  DEVICE_ID_KEYS.forEach(key => {
    if (localStorage.getItem(key) !== deviceId) {
      localStorage.setItem(key, deviceId)
    }
  })

  return deviceId
}

async function getStoredLicenseKey(): Promise<string | null> {
  try {
    const { LicenseServiceFactory } = await import('$lib/services/license/LicenseServiceFactory')
    const licenseServices = LicenseServiceFactory.createLicenseServices()
    const licenseKeys = await licenseServices.storage.listLicenseKeys()
    return licenseKeys[0] || null
  } catch (error) {
    console.warn('Failed to read stored license for request metadata:', error)
    return null
  }
}

async function getCurrentTier(licenseKey: string | null): Promise<SubscriptionType> {
  if (licenseKey?.toUpperCase().startsWith('BETA-')) {
    return 'beta'
  }

  try {
    const { SubscriptionService } = await import('$lib/services/subscription/SubscriptionService')
    const status = SubscriptionService.getInstance().getStatus()
    if (status?.type) {
      return status.type
    }
  } catch (error) {
    console.warn('Failed to read subscription tier for request metadata:', error)
  }

  return licenseKey ? 'premium' : 'free'
}

export async function getSubscriptionRequestMetadata(): Promise<SubscriptionRequestMetadata> {
  const deviceId = getOrCreateDeviceId()
  const licenseKey = await getStoredLicenseKey()
  const tier = await getCurrentTier(licenseKey)

  return {
    deviceId,
    tier,
    licenseKey
  }
}

export function buildSubscriptionHeaders(
  metadata: SubscriptionRequestMetadata
): Record<string, string> {
  return {
    'X-Device-ID': metadata.deviceId,
    'X-Device-Id': metadata.deviceId,
    'X-Subscription-Tier': metadata.tier
  }
}

export async function getSubscriptionRequestHeaders(): Promise<Record<string, string>> {
  return buildSubscriptionHeaders(await getSubscriptionRequestMetadata())
}
